import React from 'react';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Code, Sparkles, Rocket } from 'lucide-react';

const ServicesSection = () => {
  return (
    <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <Badge className="bg-purple-600/20 text-purple-400 border-purple-600/30 mb-4" data-testid="services-badge">
            What We Do
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="services-title">
            Our Services
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto" data-testid="services-description">
            Full-spectrum digital product development, from social platforms to enterprise applications.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <Card className="bg-slate-800/50 border-slate-700" data-testid="service-card-fullstack">
            <CardHeader>
              <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center mb-4">
                <Code className="w-6 h-6 text-blue-400" />
              </div>
              <CardTitle className="text-white">Web Application Development</CardTitle>
              <CardDescription className="text-slate-300">
                Full-stack applications from social networks to SaaS platforms. 
                Custom-built with modern architecture, scalability, and security built-in.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700" data-testid="service-card-ai">
            <CardHeader>
              <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-purple-400" />
              </div>
              <CardTitle className="text-white">Social & Influencer Platforms</CardTitle>
              <CardDescription className="text-slate-300">
                Premium social networks and influencer-driven platforms with community engagement, 
                content management, and monetization capabilities.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700" data-testid="service-card-mvp">
            <CardHeader>
              <div className="w-12 h-12 bg-green-600/20 rounded-lg flex items-center justify-center mb-4">
                <Rocket className="w-6 h-6 text-green-400" />
              </div>
              <CardTitle className="text-white">Rapid MVP Development</CardTitle>
              <CardDescription className="text-slate-300">
                Launch your minimum viable product quickly to validate ideas, 
                test market fit, and iterate based on real user feedback.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700" data-testid="service-card-mobile">
            <CardHeader>
              <div className="w-12 h-12 bg-orange-600/20 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <CardTitle className="text-white">E-Commerce & Marketplaces</CardTitle>
              <CardDescription className="text-slate-300">
                Custom online stores, multi-vendor marketplaces, and booking platforms 
                with payment integration and inventory management.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700" data-testid="service-card-api">
            <CardHeader>
              <div className="w-12 h-12 bg-pink-600/20 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <CardTitle className="text-white">Enterprise Solutions</CardTitle>
              <CardDescription className="text-slate-300">
                Internal tools, dashboards, workflow automation, and business intelligence 
                systems tailored to your organization's needs.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700" data-testid="service-card-consulting">
            <CardHeader>
              <div className="w-12 h-12 bg-cyan-600/20 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <CardTitle className="text-white">AI-Powered Features</CardTitle>
              <CardDescription className="text-slate-300">
                Intelligent recommendations, content moderation, chatbots, 
                and automated workflows that enhance user experience.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;